<app-maintenance-page *ngIf="isAppInMaintenance"></app-maintenance-page>

<ng-container *ngIf="!isAppInMaintenance">
<div *ngIf="isWorkspaceBlocked && !showSelectionOverlay" class="fixed inset-0 flex items-center justify-center p-8 bg-gray-50 dark:bg-gray-900" style="z-index: 1500;">
    <div class="max-w-lg rounded-2xl bg-card p-8 shadow text-center">
        <h2 class="text-xl font-semibold mb-3">Workspace unavailable</h2>
        <p class="text-secondary mb-6">{{ workspaceBlockedMessage }}</p>
        <button type="button" class="px-4 py-2 rounded bg-primary text-white" (click)="signOut()">Sign out</button>
    </div>
</div>

<platform-global-message *ngIf="shouldShowPlatformMessage"></platform-global-message>
<ashcorp-lifecycle-banner *ngIf="shouldRenderWorkspaceContent" [lifecycle]="workspaceLifecycle"></ashcorp-lifecycle-banner>

<div [class.hidden]="!shouldRenderWorkspaceContent" [attr.aria-hidden]="!shouldRenderWorkspaceContent">
    <router-outlet></router-outlet>
</div>

<div
    *ngIf="showSelectionOverlay"
    class="fixed inset-0 workspace-menu-overlay overflow-y-auto"
    style="z-index: 2000;">
    <div class="dashboard-page p-6 sm:p-10 min-h-full">
        <div class="max-w-6xl mx-auto space-y-6">
            <section class="rounded-2xl bg-card p-6 shadow hero-panel">
                <div class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <h1 class="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Choose your workspace</h1>
                        <p class="mt-3 text-secondary">
                            Select the organization and branch you want to work with before continuing.
                        </p>
                    </div>

                    <div class="flex flex-wrap items-center gap-2 text-sm">
                        <span class="rounded-full px-3 py-1 font-medium"
                            [ngClass]="selectionStep === 'organization' ? 'bg-primary text-white' : 'workspace-step-inactive'">
                            1. Organization
                        </span>
                        <span class="rounded-full px-3 py-1 font-medium"
                            [ngClass]="selectionStep === 'branch' ? 'bg-primary text-white' : 'workspace-step-inactive'">
                            2. Branch
                        </span>
                    </div>
                </div>
            </section>

            <section *ngIf="selectionStep === 'no-organization'" class="rounded-2xl bg-card p-6 shadow">
                <div class="rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100">
                    <div class="text-lg font-semibold">No organization assigned</div>
                    <p class="mt-2 text-sm leading-6">
                        Contact your administrator to assign you to an organization before you can use the system.
                    </p>
                </div>
            </section>

            <section *ngIf="selectionStep === 'organization'" class="rounded-2xl bg-card p-6 shadow">
                <div class="mb-4 text-base font-medium workspace-section-title">Available organizations</div>

                <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <button
                        *ngFor="let organization of organizations; trackBy: trackByCode"
                        type="button"
                        class="workspace-choice-card rounded-2xl border border-gray-200 px-5 py-5 text-left transition hover:border-primary hover:shadow-md dark:border-gray-700 dark:hover:border-primary"
                        (click)="chooseOrganization(organization)">
                        <div class="flex items-center gap-3">
                            <img
                                *ngIf="organization?.logo"
                                [src]="organization.logo"
                                [alt]="(organization?.name || organization?.code || 'Organization') + ' logo'"
                                class="h-10 w-10 rounded-lg object-cover border border-gray-200 dark:border-gray-700" />
                            <div
                                *ngIf="!organization?.logo"
                                class="h-10 w-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center text-sm font-bold uppercase">
                                {{ (organization?.name || organization?.code || 'O') | slice:0:1 }}
                            </div>
                            <div class="text-base font-semibold workspace-card-title">
                                {{ organization?.name || organization?.code }}
                            </div>
                        </div>
                        <div class="mt-2 text-sm workspace-card-subtitle">
                            {{ organization?.branches?.length || 0 }} branch{{ (organization?.branches?.length || 0) === 1 ? '' : 'es' }} available
                        </div>
                    </button>
                </div>
            </section>

            <section *ngIf="selectionStep === 'branch'" class="rounded-2xl bg-card p-6 shadow space-y-4">
                <div class="workspace-meta-panel flex flex-col gap-3 rounded-xl px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div class="flex items-center gap-3">
                        <img
                            *ngIf="activeOrganization?.logo"
                            [src]="activeOrganization.logo"
                            [alt]="(activeOrganization?.name || activeOrganization?.code || 'Organization') + ' logo'"
                            class="h-10 w-10 rounded-lg object-cover border border-gray-200 dark:border-gray-700" />
                        <div
                            *ngIf="!activeOrganization?.logo"
                            class="h-10 w-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center text-sm font-bold uppercase">
                            {{ (activeOrganization?.name || activeOrganization?.code || 'O') | slice:0:1 }}
                        </div>
                        <div>
                            <div class="text-xs font-semibold uppercase tracking-wide workspace-card-subtitle">Organization</div>
                            <div class="text-base font-semibold workspace-card-title">
                                {{ activeOrganization?.name || activeOrganization?.code }}
                            </div>
                        </div>
                    </div>

                    <button
                        *ngIf="organizations.length > 1"
                        type="button"
                        class="workspace-secondary-btn rounded-lg px-3 py-2 text-sm font-medium transition"
                        (click)="backToOrganizations()">
                        Change organization
                    </button>
                </div>

                <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <button
                        *ngFor="let branch of activeBranches; trackBy: trackByCode"
                        type="button"
                        class="workspace-choice-card rounded-2xl border border-gray-200 px-5 py-5 text-left transition hover:border-primary hover:shadow-md dark:border-gray-700 dark:hover:border-primary"
                        (click)="selectBranch(branch)">
                        <div class="flex items-center gap-3">
                            <img
                                *ngIf="branch?.logo"
                                [src]="branch.logo"
                                [alt]="(branch?.name || branch?.code || 'Branch') + ' logo'"
                                class="h-10 w-10 rounded-lg object-cover border border-gray-200 dark:border-gray-700" />
                            <div
                                *ngIf="!branch?.logo"
                                class="h-10 w-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center text-sm font-bold uppercase">
                                {{ (branch?.name || branch?.code || 'B') | slice:0:1 }}
                            </div>
                            <div class="text-base font-semibold workspace-card-title">
                                {{ branch?.name || branch?.code }}
                            </div>
                        </div>
                        <div class="mt-2 text-sm workspace-card-subtitle">
                            {{ branch?.code || 'Branch code unavailable' }}
                        </div>
                    </button>
                </div>
            </section>

            <section *ngIf="selectionStep === 'no-branch'" class="rounded-2xl bg-card p-6 shadow">
                <div class="rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100">
                    <div class="text-lg font-semibold">No branch assigned</div>
                    <p class="mt-2 text-sm leading-6">
                        {{ activeOrganization?.name || 'This organization' }} does not have any available branches for your account.
                        Contact your administrator to grant branch access.
                    </p>
                </div>
            </section>

            <section class="rounded-2xl bg-card p-4 shadow">
                <div class="flex justify-end">
                    <button
                        *ngIf="impersonating"
                        type="button"
                        class="workspace-action-btn rounded-lg px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
                        (click)="unimpersonate()">
                        Unimpersonate
                    </button>
                    <button
                        *ngIf="!impersonating"
                        type="button"
                        class="workspace-action-btn rounded-lg px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
                        (click)="signOut()">
                        Sign out
                    </button>
                </div>
            </section>
        </div>
    </div>
</div>
</ng-container>